import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { HotTable } from '@handsontable/react-wrapper';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/styles/handsontable.css';
import 'handsontable/styles/ht-theme-main.css';
import './FindNaverRank.css';
import '../FindNaverRankTracking/FindNaverRankTracking.css';
import '../../styles/hot-table-status-pills.css';
import AuthService from '../../services/authService';
import { hotStatusPillRenderer } from '../../utils/hotTableStatusRenderer';
import { dispatchQuotaUpdated } from '../../utils/quotaEvent';
import ProtectedMenuPage from '../../components/ProtectedMenuPage/ProtectedMenuPage';
import KakaoFloatButton from '../../components/KakaoFloatButton/KakaoFloatButton';
import { Card, Row, Col, Button, Modal, List, Spin, Empty, Typography, Space, message } from 'antd';
import {
    HistoryOutlined,
    SearchOutlined,
    ReloadOutlined,
    DeleteOutlined,
    ClockCircleOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
} from '@ant-design/icons';
registerAllModules();
const COL_WIDTHS_STORAGE_KEY = 'findNaverRank_colWidths';
const DEFAULT_COL_WIDTHS = [300, 100, 100, 80, 100];

const loadSavedColWidths = () => {
    try {
        const saved = localStorage.getItem(COL_WIDTHS_STORAGE_KEY);
        if (!saved) return null;
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) && parsed.length === DEFAULT_COL_WIDTHS.length ? parsed : null;
    } catch {
        return null;
    }
};

const saveColWidths = widths => {
    try {
        localStorage.setItem(COL_WIDTHS_STORAGE_KEY, JSON.stringify(widths));
    } catch (e) {
        console.warn('colWidths 저장 실패:', e);
    }
};

const FindNaverRank = ({ title: _title = '네이버 순위 조회' }) => {
    const { Text } = Typography;
    const [loading, setLoading] = useState(false);
    const hotRef = useRef(null);
    const [maxRows, setMaxRows] = useState(20);
    const [colWidths, setColWidths] = useState(() => {
        const saved = loadSavedColWidths();
        return saved ? [...saved] : [...DEFAULT_COL_WIDTHS];
    });
    const [tableData, setTableData] = useState(() =>
        Array.from({ length: 20 }, () => Array(5).fill(''))
    );
    const pollingRef = useRef(null);
    /** setState 전에도 연속 클릭으로 중복 요청·중복 차감 방지 */
    const rankSearchLockRef = useRef(false);

    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [appliedInputs, setAppliedInputs] = useState({});
    const [appliedInputsMid, setAppliedInputsMid] = useState({});

    const [historyData, setHistoryData] = useState([]);
    const [historyDataGubun, setHistoryDataGubun] = useState('findnaverrank');
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const PAGE_SIZE = 10;
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const limitRes = await AuthService.authAxios.get('/settings/rank-limit', {
                    params: { gubun: 'findNaverRank' },
                });
                const cap = limitRes?.data?.success ? Number(limitRes.data.max_rows) || 20 : 20;
                if (!cancelled) setMaxRows(cap);
            } catch {
                if (!cancelled) setMaxRows(20);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        setTableData(prev => {
            const next = prev.slice(0, maxRows);
            while (next.length < maxRows) next.push(Array(5).fill(''));
            return next;
        });
    }, [maxRows]);

    const handleNaverRankSearch = async () => {
        if (loading || rankSearchLockRef.current) return;
        const currentData = [...tableData];
        const lines = currentData
            .slice(0, maxRows)
            .filter(row => row[0] && row[1])
            .map(row => `${row[0]}\t${row[1]}\t${row[2] || ''}`);

        if (lines.length === 0) {
            message.warning(
                `키워드와 단일 MID를 모두 입력한 행이 1~${maxRows}번 범위 안에 1개 이상 필요합니다.`
            );
            return;
        }

        rankSearchLockRef.current = true;
        setLoading(true);

        const hot = hotRef.current?.hotInstance;
        if (hot) {
            currentData.forEach((row, idx) => {
                if (idx < maxRows && row[0] && row[1]) {
                    currentData[idx][4] = '<div class="loading-spinner"></div>';
                    hot.setCellMeta(idx, 4, 'className', 'processing-row');
                }
            });
            setTableData([...currentData]);
            hot.render();
        }

        try {
            const response = await AuthService.authAxios.post('/product/naver-rank/proxy/process', {
                lines,
                tracking: false,
            });
            const requestId = response.data.request_id;
            if (!requestId) {
                rankSearchLockRef.current = false;
                message.error('응답에 요청 ID가 없습니다.');
                setLoading(false);
                if (hot) {
                    currentData.forEach((row, idx) => {
                        if (idx < maxRows && row[0] && row[1]) {
                            currentData[idx][4] = '';
                            hot.setCellMeta(idx, 4, 'className', '');
                        }
                    });
                    setTableData([...currentData]);
                    hot.render();
                }
                return;
            }
            dispatchQuotaUpdated('findNaverRank');
            localStorage.setItem('naver_request_id', requestId);
            startPolling(requestId);
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || '서버 오류가 발생했습니다.';
            message.error(msg);
            rankSearchLockRef.current = false;
            setLoading(false);
            if (hot) {
                currentData.forEach((row, idx) => {
                    if (idx < maxRows && row[0] && row[1]) {
                        currentData[idx][4] = '';
                        hot.setCellMeta(idx, 4, 'className', '');
                    }
                });
                setTableData([...currentData]);
                hot.render();
            }
        }
    };

    const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

    const updateLogHistory = async results => {
        const uuid = uuidv4();
        for (const res of results) {
            try {
                await AuthService.authAxios.post('/log/history-update', {
                    type: 'findnaverrank',
                    inputLog: `keyword=${res.keyword}|product_id=${res.product_id}|product_id2=${res.product_id2}|rank=${res.rank}|uuid=${uuid}`,
                });
                await delay(300); // 0.5초 텀
            } catch (err) {
                console.error('순위 기록 저장 실패:', err);
            }
        }
    };

    const startPolling = requestId => {
        if (pollingRef.current) clearInterval(pollingRef.current);

        pollingRef.current = setInterval(async () => {
            try {
                const res = await AuthService.authAxios.get(
                    `/product/naver-rank/proxy/status/${requestId}`
                );
                const { status, result } = res.data;
                if (status === 'done') {
                    clearInterval(pollingRef.current);
                    pollingRef.current = null;
                    localStorage.removeItem('naver_request_id');
                    applyResultToTable(result);

                    await updateLogHistory(result);

                    message.success('순위 조회가 완료되었습니다.');
                    rankSearchLockRef.current = false;
                    setLoading(false);
                }
            } catch (e) {
                console.error('상태 확인 오류:', e);
                message.error('상태 확인 중 오류가 발생했습니다.');
                clearInterval(pollingRef.current);
                pollingRef.current = null;
                rankSearchLockRef.current = false;
                setLoading(false);
            }
        }, 2000);
    };

    const applyResultToTable = results => {
        const newData = [...tableData];
        results.forEach(res => {
            const index = newData.findIndex(
                row => row[0] === res.keyword && row[1] === res.product_id
            );
            if (index !== -1) {
                newData[index][3] = res.rank || '';
                newData[index][4] = res.error ? `❌ ${res.error}` : '성공';
            }
        });
        setTableData(newData);
        hotRef.current?.hotInstance?.render();
    };

    useEffect(() => {
        const savedId = localStorage.getItem('naver_request_id');
        if (savedId) startPolling(savedId);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleReset = () => {
        setTableData([...Array.from({ length: maxRows }, () => Array(5).fill(''))]);
    };

    const _handleSubmit = e => {
        e.preventDefault();
        handleNaverRankSearch();
    };

    const handleDelete = async id => {
        Modal.confirm({
            title: '정말 삭제하시겠습니까?',
            content: '삭제된 기록은 복구할 수 없습니다.',
            okText: '삭제',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                try {
                    await AuthService.authAxios.post('/log/delete', { id });
                    setHistoryData(prev => prev.filter(item => item.id !== id));
                    message.success('삭제되었습니다.');
                } catch (err) {
                    console.error('삭제 실패:', err);
                    message.error('삭제에 실패했습니다.');
                }
            },
        });
    };

    const fetchHistory = async (nextOffset = 0, append = false, gubun = 'findnaverrank') => {
        try {
            setHistoryDataGubun(gubun);
            const res = await AuthService.authAxios.get('/log/history', {
                params: {
                    gubun: gubun,
                    offset: nextOffset,
                },
            });

            if (res.data.success) {
                const newData = res.data.data;
                if (!append) {
                    setHasMore(true); // ✅ 새로 로딩할 땐 초기화
                }
                if (newData.length < PAGE_SIZE) setHasMore(false);

                if (append) {
                    setHistoryData(prev => [...prev, ...newData]);
                    setOffset(prev => prev + PAGE_SIZE);
                } else {
                    setHistoryData(newData);
                    setOffset(PAGE_SIZE);
                }
            }
        } catch (err) {
            console.error('조회 기록 불러오기 실패:', err);
        } finally {
            setHistoryLoaded(true);
        }
    };

    const handleApplyInput = (inputString, gubun = 'findnaverrank') => {
        const values = Object.fromEntries(inputString.split('|').map(pair => pair.split('=')));
        const newData = [...tableData];

        if (gubun === 'findnaverrank') {
            const emptyRowIndex = newData
                .slice(0, maxRows)
                .findIndex(row => !row[0] && !row[1] && !row[2]);
            if (emptyRowIndex === -1) {
                message.warning(`빈 행이 없습니다. (최대 ${maxRows}행)`);
                return;
            }
            newData[emptyRowIndex][0] = values.keyword || '';
            newData[emptyRowIndex][1] = values.product_id || '';
            newData[emptyRowIndex][2] = values.product_id2 || '';

            setAppliedInputs(prev => ({ ...prev, [inputString]: true }));
        } else {
            const emptyRowIndex = newData
                .slice(0, maxRows)
                .findIndex(row => !row[0] && !row[1] && !row[2]);
            if (emptyRowIndex === -1) {
                message.warning(`빈 행이 없습니다. (최대 ${maxRows}행)`);
                return;
            }
            newData[emptyRowIndex][1] = values.syncNvMid || '';
            newData[emptyRowIndex][2] = values.parentId || '';

            setAppliedInputsMid(prev => ({ ...prev, [inputString]: true }));
        }
        setTableData(newData);
        //setShowHistoryModal(false);
    };

    return (
        <ProtectedMenuPage menuKey="findNaverRank">
            <div className="find-naver-rank-page">
                <div className="page-header-container no-header-border">
                    <Card className="control-card" bodyStyle={{ padding: '16px 20px' }}>
                        <Row justify="space-between" align="middle" gutter={16}>
                            <Col>
                                <Text type="secondary">사용(조회) 가능 행 수: </Text>
                                <Text strong>{maxRows}</Text>
                            </Col>
                            <Col>
                                <Space size="middle">
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<SearchOutlined />}
                                        onClick={handleNaverRankSearch}
                                        loading={loading}
                                        disabled={loading}
                                        style={{ minWidth: '120px' }}
                                    >
                                        조회 시작
                                    </Button>
                                    <Button
                                        className="page-action-btn"
                                        size="large"
                                        icon={<ReloadOutlined />}
                                        onClick={handleReset}
                                    >
                                        초기화
                                    </Button>
                                    <Button
                                        className="history-btn"
                                        icon={<HistoryOutlined />}
                                        onClick={() => {
                                            setShowHistoryModal(true);
                                            fetchHistory(0, false, 'findnaverrank');
                                        }}
                                    >
                                        조회 기록
                                    </Button>
                                    <Button
                                        className="history-btn"
                                        icon={<FileTextOutlined />}
                                        onClick={() => {
                                            setShowHistoryModal(true);
                                            fetchHistory(0, false, 'findmid');
                                        }}
                                    >
                                        MID 기록
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Card>
                </div>

                <div className="page-content-inner">
                    <div className="naver-rank-tracking-table-wrapper">
                        <div className="naver-rank-tracking-table">
                            <HotTable
                                ref={hotRef}
                                data={tableData}
                                colHeaders={[
                                    '키워드 <span class="text-danger">*</span>',
                                    '단일 MID <span class="text-danger">*</span>',
                                    '원부 MID',
                                    '순위',
                                    '상태',
                                ]}
                                contextMenu={{
                                    items: {
                                        undo: {
                                            name: '되돌리기',
                                            hidden: function () {
                                                return !this.getPlugin(
                                                    'undoRedo'
                                                )?.isUndoAvailable();
                                            },
                                        },
                                        redo: {
                                            name: '다시 실행하기',
                                            hidden: function () {
                                                return !this.getPlugin(
                                                    'undoRedo'
                                                )?.isRedoAvailable();
                                            },
                                        },
                                        copy: { name: '복사' },
                                        cut: { name: '잘라내기' },
                                    },
                                }}
                                rowHeaders={true}
                                autoWrapRow={true}
                                autoWrapCol={true}
                                stretchH="all"
                                height="100%"
                                width="100%"
                                minRows={maxRows}
                                maxRows={maxRows}
                                colWidths={colWidths}
                                manualColumnResize={true}
                                afterColumnResize={(newSize, column) => {
                                    setColWidths(prev => {
                                        const next = [...prev];
                                        next[column] = newSize;
                                        saveColWidths(next);
                                        return next;
                                    });
                                }}
                                cells={(row, col) => {
                                    const overCap = row >= maxRows;
                                    const isCenter = col >= 1 && col <= 4;
                                    const base = overCap
                                        ? { readOnly: true }
                                        : col >= 3 && col <= 4
                                          ? {
                                                readOnly: true,
                                                renderer:
                                                    col === 4 ? hotStatusPillRenderer : undefined,
                                            }
                                          : col <= 1
                                            ? { readOnly: false, className: 'required-column' }
                                            : { readOnly: false };
                                    return {
                                        ...base,
                                        className: [base.className, isCenter ? 'htCenter' : null]
                                            .filter(Boolean)
                                            .join(' '),
                                    };
                                }}
                                beforeChange={(changes, source) => {
                                    if (!changes) return;
                                    const overCap = changes.some(([row]) => row >= maxRows);
                                    if (overCap) {
                                        message.warning(
                                            `최대 ${maxRows}행까지만 편집할 수 있습니다.`
                                        );
                                        return false;
                                    }
                                    const isPaste = source === 'CopyPaste.paste';
                                    if (isPaste) {
                                        const maxRowTouched = Math.max(
                                            ...changes.map(([row]) => row)
                                        );
                                        if (maxRowTouched + 1 > maxRows) {
                                            message.warning(
                                                `⚠️ 최대 ${maxRows}개 행까지만 입력할 수 있습니다.`
                                            );
                                            return false;
                                        }
                                    }
                                }}
                                licenseKey="non-commercial-and-evaluation"
                            />
                        </div>
                    </div>
                </div>

                <Modal
                    title={
                        <Space>
                            <HistoryOutlined />
                            <span>
                                {historyDataGubun === 'findnaverrank'
                                    ? '최근 조회 기록'
                                    : '최근 MID 기록'}
                            </span>
                        </Space>
                    }
                    open={showHistoryModal}
                    onCancel={() => setShowHistoryModal(false)}
                    centered
                    mask={{ blur: true }}
                    footer={[
                        <Button key="close" onClick={() => setShowHistoryModal(false)}>
                            닫기
                        </Button>,
                    ]}
                    width={800}
                    className="history-modal"
                    bodyStyle={{ maxHeight: '600px', overflowY: 'auto' }}
                >
                    <Spin spinning={!historyLoaded}>
                        <List
                            itemLayout="horizontal"
                            dataSource={historyData}
                            locale={{ emptyText: <Empty description="기록이 없습니다." /> }}
                            renderItem={item => {
                                const parts = {};
                                (item.input || '').split('|').forEach(pair => {
                                    const [key, value] = pair.split('=');
                                    if (key) parts[key] = value;
                                });

                                const isNaverRank = historyDataGubun === 'findnaverrank';
                                const isApplied = isNaverRank
                                    ? appliedInputs[item.input]
                                    : appliedInputsMid[item.input];

                                return (
                                    <List.Item
                                        actions={[
                                            <Button
                                                key="apply"
                                                type={isApplied ? 'primary' : 'default'}
                                                size="small"
                                                icon={isApplied ? <CheckCircleOutlined /> : null}
                                                onClick={() =>
                                                    handleApplyInput(item.input, historyDataGubun)
                                                }
                                                disabled={isApplied}
                                            >
                                                {isApplied
                                                    ? '적용됨'
                                                    : isNaverRank
                                                      ? '순위 조회 적용'
                                                      : 'MID 적용'}
                                            </Button>,
                                            <Button
                                                key="delete"
                                                type="link"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => handleDelete(item.id)}
                                            >
                                                삭제
                                            </Button>,
                                        ]}
                                    >
                                        <List.Item.Meta
                                            avatar={
                                                <ClockCircleOutlined
                                                    style={{
                                                        fontSize: '20px',
                                                        color: '#bfbfbf',
                                                        marginTop: '4px',
                                                    }}
                                                />
                                            }
                                            title={
                                                isNaverRank ? (
                                                    <Space direction="vertical" size={0}>
                                                        <Text strong>
                                                            키워드: {parts.keyword || '-'}
                                                        </Text>
                                                        <Text size="small">
                                                            순위 :{' '}
                                                            <Text
                                                                strong
                                                                type={
                                                                    parts.rank && parts.rank !== '0'
                                                                        ? 'primary'
                                                                        : 'secondary'
                                                                }
                                                            >
                                                                {parts.rank || '-'}
                                                            </Text>
                                                        </Text>
                                                    </Space>
                                                ) : (
                                                    <Space direction="vertical" size={0}>
                                                        <Text strong>
                                                            {parts.product_name || '상품명 없음'}
                                                        </Text>
                                                        <Text
                                                            type="secondary"
                                                            style={{ fontSize: '12px' }}
                                                        >
                                                            {parts.channel_name || '-'}
                                                        </Text>
                                                    </Space>
                                                )
                                            }
                                            description={
                                                <Space direction="vertical" size={0}>
                                                    {isNaverRank ? (
                                                        <Text size="small" type="secondary">
                                                            단일 MID: {parts.product_id || '-'} /
                                                            원부 MID: {parts.product_id2 || '-'}
                                                        </Text>
                                                    ) : (
                                                        <>
                                                            <Text
                                                                size="small"
                                                                type="secondary"
                                                                ellipsis={{ tooltip: parts.url }}
                                                            >
                                                                URL: {parts.url || '-'}
                                                            </Text>
                                                            <Text size="small" type="secondary">
                                                                단일 MID: {parts.syncNvMid || '-'} /
                                                                원부 MID: {parts.parentId || '-'}
                                                            </Text>
                                                        </>
                                                    )}
                                                    <Text
                                                        size="small"
                                                        type="secondary"
                                                        style={{ fontSize: '11px' }}
                                                    >
                                                        {item.updated_at || item.searched_at}
                                                    </Text>
                                                </Space>
                                            }
                                        />
                                    </List.Item>
                                );
                            }}
                        />
                        {hasMore && historyData.length > 0 && (
                            <div style={{ textAlign: 'center', marginTop: '16px' }}>
                                <Button
                                    onClick={() => fetchHistory(offset, true, historyDataGubun)}
                                >
                                    더보기
                                </Button>
                            </div>
                        )}
                    </Spin>
                </Modal>
            </div>
            <KakaoFloatButton />
        </ProtectedMenuPage>
    );
};

export { FindNaverRank };
FindNaverRank.propTypes = {
    title: PropTypes.string,
};
